import PropTypes from 'prop-types'
import { Navigate, useLocation } from 'react-router-dom'
// hooks
import usePermissions from '../hooks/usePermissions'
// components
import LoadingScreen from '../components/LoadingScreen'
// routes
import { PATH_DASHBOARD } from '../routes/paths'

// ----------------------------------------------------------------------

// Per-user permission gate for everything under /libra. Sits inside AuthGuard.
//
// A user without can_analyse (an upload-only account) works on the Files page
// only: their uploads, verification and file QC. Every other screen (home and the
// analysis list, result views, batches, reports) sends them to Files. Their own
// account settings stay reachable, and an organisation admin keeps the
// organisation page (user management, balance).
//
// Users with can_analyse see every screen they saw before. The server checks the
// same rules on every endpoint; this only keeps people off screens that would
// fail for them.

const startsWithAny = (pathname, prefixes) =>
  prefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))

// Open to anyone signed in, whatever their permissions.
const ALWAYS_ALLOWED = [PATH_DASHBOARD.general.files, PATH_DASHBOARD.user.account]

// Organisation management, for admins whatever their analysis permission.
const ADMIN_ALLOWED = [
  // The organisation page, adding and editing users.
  PATH_DASHBOARD.user.root,
  '/libra/payment-success',
  '/libra/payment-failed',
  '/libra/invoice',
]

export const isPathAllowed = (pathname, { canAnalyse, isOrganisationAdmin }) => {
  if (canAnalyse) return true
  if (startsWithAny(pathname, ALWAYS_ALLOWED)) return true
  if (isOrganisationAdmin && startsWithAny(pathname, ADMIN_ALLOWED)) return true
  return false
}

PermissionGuard.propTypes = {
  children: PropTypes.node,
}

export default function PermissionGuard({ children }) {
  const { pathname } = useLocation()
  const { isLoading, canAnalyse, isOrganisationAdmin, homePath } = usePermissions()

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!isPathAllowed(pathname, { canAnalyse, isOrganisationAdmin: Boolean(isOrganisationAdmin) })) {
    return <Navigate to={homePath} replace />
  }

  return <>{children}</>
}
